import { useEffect, useState } from 'react';
import { authManager } from './auth';
import { isNonMmlAdmin } from './roles';

// admin#175 — OIDC partner clients (somo-circle1-api src/modules/oidc).
//
// ⚠️ The register and rotate responses carry a plaintext client secret exactly
// once. Nothing in this module may log a response body, and callers must keep
// the secret in component state only — never localStorage/sessionStorage, the
// URL, or a context that outlives the one-time panel.

export const DEFAULT_SCOPES = ['openid', 'profile', 'email'];

// Same rule as the API: the clientId is derived from the name, so anything
// else (brackets were the real-world case) would end up in it. Hyphens and
// underscores (api#758) are URL-safe and kept in the clientId, but the name
// must start with a letter or digit, so the clientId never starts with - or _.
const CLIENT_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,99}$/;
export const CLIENT_NAME_RULE = 'Use letters, digits, spaces, hyphens and underscores only, '
    + 'starting with a letter or digit (max 100 characters).';

// Same as the API's SCOPE_PATTERN, so the form can name the bad scope instead
// of the API's generic "allowedScopes must be an array of scope names".
const SCOPE_PATTERN = /^[A-Za-z0-9_:.\-/]{1,100}$/;

const clientsUrl = (path = '') => `${process.env.REACT_APP_API_BASE_URL}/oauth/clients${path}`;

// The API wraps payloads in `result` (not `data`); a validation error carries
// its human-readable text in error.errors, other errors in error.message.
// Shared with utils/partnerInquiries.js, whose contact routes do the same.
export async function requestResult(url, options = {}) {
    const res = await authManager.authenticatedFetch(url, {
        ...options,
        headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
    });
    const body = await res.json().catch(() => null);
    if (body?.status === 'success') return body.result;
    const message = (typeof body?.error?.errors === 'string' && body.error.errors)
        || body?.error?.message
        || body?.message
        || `Request failed (${res.status})`;
    throw new Error(message);
}

const request = (path, options) => requestResult(clientsUrl(path), options);

// partnerContactId (api#766) narrows the list to the clients registered for
// one partner inquiry.
export const listClients = ({
    page = 1, limit = 10, status, partnerContactId,
} = {}) => {
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) qs.set('status', status);
    if (partnerContactId) qs.set('partnerContactId', partnerContactId);
    return request(`/list?${qs}`);
};

export const getClient = (clientId) => request(`/${encodeURIComponent(clientId)}`);

export const registerClient = (payload) => request('/register', {
    method: 'POST',
    body: JSON.stringify(payload),
});

export const updateClient = (clientId, changes) => request(`/${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    body: JSON.stringify(changes),
});

export const revokeClient = (clientId) => request(`/${encodeURIComponent(clientId)}`, { method: 'DELETE' });

export const rotateClientSecret = (clientId) => request(`/${encodeURIComponent(clientId)}/rotate-secret`, {
    method: 'POST',
});

export const listClientAudit = (clientId, { page = 1, limit = 20 } = {}) => {
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    return request(`/${encodeURIComponent(clientId)}/audit?${qs}`);
};

export const isValidClientName = (name) => CLIENT_NAME_PATTERN.test((name || '').trim());

// HTTPS only, except http:// on a loopback host. The API allows those outside
// production and rejects them in production, so the API decides per
// environment. (NODE_ENV can't: every deployed build of this app, staging
// included, is a production build.)
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

export function redirectUriError(uri) {
    let parsed;
    try {
        parsed = new URL(uri);
    } catch {
        return 'Not a valid URL.';
    }
    if (parsed.hash) return 'Must not contain a #fragment.';
    if (parsed.protocol === 'https:') return null;
    if (parsed.protocol === 'http:' && LOOPBACK_HOSTS.includes(parsed.hostname)) return null;
    return 'Must use https://';
}

export const parseScopes = (text) => [...new Set((text || '').split(/[\s,]+/).filter(Boolean))];

export const invalidScopes = (scopes) => scopes.filter((s) => !SCOPE_PATTERN.test(s));

// Order doesn't matter for scopes or redirect URIs, so reordering them isn't
// a change worth sending (or an audit entry).
export const sameItems = (a, b) => a.length === b.length && a.every((v) => b.includes(v));

export const isWhitelistError = (message) => /allowed whitelist/i.test(message || '');

// null while checking, then true/false. Mirrors the sidebar's showSettings
// gate. UX only: the API applies requireNonMmlAdmin to every OIDC client
// admin route (api#754), so this just keeps an MML-sourced admin off screens
// whose calls would 403 anyway.
export function useNonMmlAdminAccess() {
    const [allowed, setAllowed] = useState(null);
    useEffect(() => {
        let active = true;
        authManager.validateUser()
            .catch(() => null)
            .then((user) => { if (active) setAllowed(isNonMmlAdmin(user)); });
        return () => { active = false; };
    }, []);
    return allowed;
}

// Rotation would give a public client (somo-cli, device flow) a secret, which
// breaks it; the API refuses, and the UI should not offer it.
export const canRotate = (client) => client.isPublicClient !== true && client.status !== 'revoked';
